import { Stack, Typography } from '@mui/material'
import { OTHER, REFRESH } from 'common/components/action/ActionConstant'
import { CardTable, SORT } from 'common/components/datatable/Table'
import { STATUS } from 'common/constants/storeConstants'
import useActions from 'common/hooks/useActions'
import useFetch from 'common/hooks/useFetch'
import useTitles from 'common/hooks/useTitles'
import { PASTEL_COLOR } from 'common/utils/ColorUtil'
import { getDate, getDuration } from 'common/utils/DateUtil'
import ApplicationConf from 'conf/ApplicationConf'
import React from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { AppActions } from 'app/appSlice'


const formatIssuesNotCovered = list => list.map(row => ({
    id: row.id,
    summary: row.summary,
    project: row.project,
    category: row.category,
    email: row.email,
    dateSubmitted: row.dateSubmitted,
}))

const formatIssuesUnanswered = list => list.map(row => ({
    id: row.id,
    summary: row.summary,
    project: row.project,
    note: row.note,
    category: row.category,
    email: row.email,
    date: row.date,
}))

const getColor = (date) => {
    const nbDays = getDuration(date)
    if (nbDays >= 365) return PASTEL_COLOR.red
    if (nbDays >= 30) return PASTEL_COLOR.orange
    if (nbDays >= 7) return PASTEL_COLOR.yellow
    return 'white'
}

const formattingTableNotCovered = list => list.map(row => ({
    id: row.id,
    idIssue: { value: row.id },
    summary: {
        value: row.summary && row.summary.length > 50
            ? `${row.summary.slice(0, 50)}...`
            : row.summary,
    },
    project: { value: row.project },
    category: { value: row.category },
    email: { value: row.email },
    dateSubmitted: {
        value: getDate(row.dateSubmitted * 1000),
        sortValue: row.dateSubmitted,
        color: getColor(row.dateSubmitted * 1000),
    },
}
))

const formattingTableUnanswered = list => list.map(row => ({
    id: row.id,
    idIssue: { value: row.id },
    summary: {
        value: row.summary && row.summary.length > 50
            ? `${row.summary.slice(0, 50)}...`
            : row.summary,
    },
    project: { value: row.project },
    note: {
        value: row.note && row.note.length > 50
            ? `${row.note.slice(0, 50)}...`
            : row.note,
    },
    category: { value: row.category },
    email: { value: row.email },
    date: {
        value: getDate(row.date * 1000),
        sortValue: row.date,
        color: getColor(row.date * 1000),
    },
}))

const headersResolvedTable = [{ key: 'idIssue', value: 'Id' }, { key: 'summary', value: 'Titre' }, { key: 'project', value: 'Projet' }, { key: 'category', value: 'Catégorie' }, { key: 'email', value: 'Email' }, { key: 'dateSubmitted', value: 'Date de soumission' }]
const headersIssuesUnansweredClient = [{ key: 'idIssue', value: 'Id' }, { key: 'summary', value: 'Titre' }, { key: 'project', value: 'Projet' }, { key: 'note', value: 'Note' }, { key: 'category', value: 'Catégorie' }, { key: 'email', value: 'Email' }, { key: 'date', value: 'Dernière modification' }]
const headersIssuesUnansweredSupport = [{ key: 'idIssue', value: 'Id' }, { key: 'summary', value: 'Titre' }, { key: 'project', value: 'Projet' }, { key: 'note', value: 'Note' }, { key: 'category', value: 'Catégorie' }, { key: 'email', value: 'Email' }, { key: 'date', value: 'Date de la note' }]

const DashboardSupportApp = () => {
    useTitles(() => [{
        label: 'Tableau de bord Support',
        href: '/dashboard',
    }], [])

    const {
        data: issuesNotCovered = [],
        status: statusNotCovered,
        reload: reloadNotCovered,
    } = useFetch(ApplicationConf.support.issuesNotCovered(), {}, { formatResult: formatIssuesNotCovered })


    const {
        data: issuesUnansweredClient = [],
        status: statusUnansweredClient,
        reload: reloadUnansweredClient,
    } = useFetch(ApplicationConf.support.issuesUnansweredClient(), {}, { formatResult: formatIssuesUnanswered })

    const {
        data: issuesUnansweredSupport = [],
        status: statusUnansweredSupport,
        reload: reloadUnansweredSupport,
    } = useFetch(ApplicationConf.support.issuesUnansweredSupport(), {}, { formatResult: formatIssuesUnanswered })

    const reloadAll = () => {
        reloadNotCovered()
        reloadUnansweredClient()
        reloadUnansweredSupport()
    }

    const dispatch = useDispatch()
    const { isNewTicketAlertMute } = useSelector(store => ({
        isNewTicketAlertMute: store.app.isNewTicketAlertMute,
    }), shallowEqual)

    useActions(() => [{
        type: REFRESH,
        onClick: reloadAll,
        loading: statusUnansweredSupport === STATUS.LOADING,
    }, {
        type: OTHER,
        icon: isNewTicketAlertMute ? 'volume_off' : 'volume_up',
        tooltip: isNewTicketAlertMute ? 'Réactiver le son des nouveaux tickets' : 'Couper le son des nouveaux tickets',
        onClick: () => dispatch(AppActions.toggleNewTicketAlertMute()),
    }], [statusUnansweredSupport, isNewTicketAlertMute])

    return (
        <Stack spacing={2}>
            <CardTable
                title={'Ticket non pris en charge'}
                actions={[
                    {
                        icon: 'help',
                        tooltip: <span style={{ whiteSpace: 'pre-wrap' }}>
                            {'Anomalie/Evolution/Assistance\nRapporter par un client\nStatut: "Nouveau"\nNon affecté à un lot\nNon affecté à "Comité arbitrage"'}
                        </span>,
                    },
                ]}
                rows={formattingTableNotCovered(issuesNotCovered)}
                color='#161832'
                rowsPerPageDefault={10}
                status={statusNotCovered}
                headers={headersResolvedTable}
                onClickRow={({ id }) => window.open(`https://support.aquasys.fr/view.php?id=${id}`, '_blank')}
                defaultSort={{ column: 'dateSubmitted', direction: SORT.DESC }}
            />
            <CardTable
                title={'Ticket en attente d\'un retour'}
                actions={[
                    {
                        icon: 'help',
                        tooltip: <span style={{ whiteSpace: 'pre-wrap' }}>
                            {'Statut: commentaire/traité/en recette\nDont le dernier commentaire a été fait par un client'}
                        </span>,
                    },
                ]}
                rows={formattingTableUnanswered(issuesUnansweredSupport)}
                color='#161832'
                rowsPerPageDefault={10}
                status={statusUnansweredSupport}
                headers={headersIssuesUnansweredSupport}
                onClickRow={({ id }) => window.open(`https://support.aquasys.fr/view.php?id=${id}`, '_blank')}
                defaultSort={{ column: 'date', direction: SORT.DESC }}
            />
            <CardTable
                title={'Ticket assigné à un client'}
                actions={[
                    {
                        icon: 'help',
                        tooltip: <span style={{ whiteSpace: 'pre-wrap' }}>
                            {'Statut: "commentaire"\nAffecté à un client\nDont le dernier commentaire a été fait par nous'}
                        </span>,
                    },
                ]}
                rows={formattingTableUnanswered(issuesUnansweredClient)}
                color='#161832'
                rowsPerPageDefault={10}
                status={statusUnansweredClient}
                headers={headersIssuesUnansweredClient}
                onClickRow={({ id }) => window.open(`https://support.aquasys.fr/view.php?id=${id}`, '_blank')}
                defaultSort={{ column: 'date', direction: SORT.DESC }}
            />
            <Typography variant='h6' fontWeight='bold'>TODO : vue graphique</Typography>
        </Stack>
    )
}
export default DashboardSupportApp